import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, SelectorIcon } from '@heroicons/react/solid';
import { Fragment } from 'react';
import clsxm from '@/lib/clsxm';

import useLocale from 'next-translate/useTranslation';

import { SimpleTooltip } from '@/components/ui/tooltip';

interface AspectRatioSelectorProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  disabledOptions?: string[];
  disabled?: boolean;
}

const AspectRatioIconFilled = ({
  ratio,
  className,
}: {
  ratio: string;
  className?: string;
}) => {
  const separator = ratio.includes('x') ? 'x' : ':';
  const [w, h] = ratio.split(separator).map(Number) || [1, 1];

  if (!w || !h) return null;

  const maxDim = 12; // slightly smaller to add padding
  let width, height;

  if (w > h) {
    width = maxDim;
    height = (h / w) * maxDim;
  } else {
    height = maxDim;
    width = (w / h) * maxDim;
  }

  const x = (16 - width) / 2;
  const y = (16 - height) / 2;

  return (
    <svg
      viewBox='0 0 16 16'
      className={className}
      fill='currentColor'
      xmlns='http://www.w3.org/2000/svg'
    >
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx={1.5}
        stroke='currentColor'
        strokeWidth='1.2'
        fill='transparent'
      />
    </svg>
  );
};

export default function AspectRatioSelector({
  value,
  onChange,
  options,
  disabledOptions = [],
  disabled = false,
}: AspectRatioSelectorProps) {
  const { t } = useLocale('common');

  // Helper to get display label
  const getLabel = (ratio: string) => {
    // Handle auto/match_input_image
    if (ratio === 'auto' || ratio === 'match_input_image') {
      return t('Auto');
    }
    // If it's pixel dimensions like 1024x1024, try to convert to ratio or show as is
    if (ratio.includes('x')) {
      if (ratio === '1024x1024') return '1:1';
      if (ratio === '1024x1792') return '9:16';
      if (ratio === '1792x1024') return '16:9';
      if (ratio === '1365x1024') return '4:3';
      if (ratio === '1024x1365') return '3:4';
      if (ratio === '1536x1024') return '3:2';
      if (ratio === '1024x1536') return '2:3';
      if (ratio === '1820x1024') return '16:9';
      if (ratio === '1024x1820') return '9:16';
      if (ratio === '1024x2048') return '1:2';
      if (ratio === '2048x1024') return '2:1';
      if (ratio === '1434x1024') return '7:5';
      if (ratio === '1024x1434') return '5:7';
      if (ratio === '1024x1280') return '4:5';
      if (ratio === '1280x1024') return '5:4';
      if (ratio === '1024x1707') return '3:5';
      if (ratio === '1707x1024') return '5:3';

      // ... add more if needed or simplify
      return ratio;
    }
    return ratio;
  };

  const getDescription = (ratio: string) => {
    const label = getLabel(ratio);
    // Handle special cases
    if (ratio === 'auto' || ratio === 'match_input_image') {
      return t('Match input');
    }
    switch (label) {
      case '1:1':
        return 'Square';
      case '16:9':
        return 'Widescreen';
      case '9:16':
        return 'Social story';
      case '2:3':
        return 'Portrait';
      case '3:2':
        return 'Standard';
      case '3:4':
        return 'Traditional';
      case '4:3':
        return 'Classic';
      case '4:5':
        return 'Social post';
      case '5:4':
        return 'Landscape';
      case '1:2':
        return 'Vertical';
      case '2:1':
        return 'Horizontal';
      case '21:9':
        return 'Ultrawide';
      case '9:21':
        return 'Tall';
      default:
        return '';
    }
  };

  const currentLabel = getLabel(value);

  return (
    <div className='relative w-auto min-w-[60px]'>
      <Listbox value={value} onChange={onChange} disabled={disabled}>
        {({ open }) => (
          <>
            <div className='relative'>
              {/* Trigger Button */}
              <Listbox.Button
                className={clsxm(
                  'relative w-full max-w-[100px] cursor-pointer rounded-xl border bg-white py-2 pe-8 ps-3 text-start shadow-sm focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm dark:bg-slate-700/50 dark:border-white/5',
                  disabled
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-gray-50 dark:hover:bg-slate-700',
                )}
              >
                <SimpleTooltip title={t('Aspect Ratio')} side='bottom'>
                  <div className='flex items-center gap-1.5'>
                    <AspectRatioIconFilled
                      ratio={currentLabel}
                      className='h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400'
                    />
                    <span className='block truncate text-gray-700 dark:text-gray-200 font-medium'>
                      {currentLabel}
                    </span>
                  </div>
                  <span className='pointer-events-none absolute inset-y-0 end-0 flex items-center pe-2'>
                    <SelectorIcon
                      className='h-5 w-5 text-gray-400'
                      aria-hidden='true'
                    />
                  </span>
                </SimpleTooltip>
              </Listbox.Button>

              {/* Dropdown Panel - Positioned upwards */}
              <Transition
                as={Fragment}
                leave='transition ease-in duration-100'
                leaveFrom='opacity-100'
                leaveTo='opacity-0'
              >
                <Listbox.Options className='absolute bottom-full mb-1 start-0 z-50 w-full min-w-[200px] max-h-64 overflow-auto rounded-xl bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm dark:bg-slate-800 dark:ring-white/10'>
                  <div className='px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider'>
                    {t('Aspect Ratio')}
                  </div>
                  {options.map((option, personIdx) => {
                    const isDisabled = disabledOptions.includes(option);
                    const label = getLabel(option);

                    return (
                      <Listbox.Option
                        key={personIdx}
                        className={({ active }) =>
                          clsxm(
                            'relative cursor-default select-none py-2 pe-4 ps-3',
                            active
                              ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-900 dark:text-indigo-100'
                              : 'text-gray-900 dark:text-gray-300',
                            isDisabled
                              ? 'opacity-50 cursor-not-allowed'
                              : 'cursor-pointer',
                          )
                        }
                        value={option}
                        disabled={isDisabled}
                      >
                        {({ selected }) => (
                          <div className='flex items-center justify-between'>
                            <div className='flex items-center gap-3'>
                              {/* Icon Box */}
                              <div
                                className={clsxm(
                                  'flex items-center justify-center w-8 h-8 rounded-md',
                                  selected
                                    ? 'bg-indigo-100 dark:bg-indigo-800 text-indigo-600 dark:text-indigo-200'
                                    : 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-400',
                                )}
                              >
                                <AspectRatioIconFilled
                                  ratio={label}
                                  className='w-5 h-5'
                                />
                              </div>
                              <span
                                className={clsxm(
                                  'block truncate font-medium',
                                  selected ? 'font-semibold' : 'font-normal',
                                )}
                              >
                                {label}
                              </span>
                              {getDescription(option) && (
                                <span className='text-xs text-gray-500 dark:text-gray-400 font-normal'>
                                  {getDescription(option)}
                                </span>
                              )}
                            </div>
                            {selected ? (
                              <span className='text-indigo-600 dark:text-indigo-400 ms-2'>
                                <CheckIcon
                                  className='h-4 w-4'
                                  aria-hidden='true'
                                />
                              </span>
                            ) : null}
                          </div>
                        )}
                      </Listbox.Option>
                    );
                  })}
                </Listbox.Options>
              </Transition>
            </div>
          </>
        )}
      </Listbox>
    </div>
  );
}
